import {ReactNode} from 'react';
import {useIntl} from 'react-intl';
import {Flex, Image, Text, FlexProps} from '@chakra-ui/react';

import {useTranslate} from '@eksab/i18n';
import {TShirtIcon} from '@eksab/assets/icons';

import {PlayerWarningIndicator} from './PlayerWarningIndicator';
import {CaptainIndicator} from '../components';
import type {Player} from '../types';

type PlayerItemProps = FlexProps & {
  action: ReactNode;
  onPlayerClick: () => void;
  isSelected?: boolean;
  inLineup?: boolean;
} & (
    | {
        player: Player;
        placeholderPosition?: Player['position'];
      }
    | {
        player: null;
        placeholderPosition: Player['position'];
      }
  );

export function PlayerItem({
  player,
  onPlayerClick,
  action,
  isSelected = false,
  inLineup = false,
  placeholderPosition,
  ...props
}: PlayerItemProps) {
  return (
    <Flex
      w="full"
      p="3"
      borderWidth="2px"
      borderColor={isSelected ? 'green.400' : 'gray.200'}
      rounded="md"
      align="center"
      justify="space-between"
      gap="3"
      cursor="pointer"
      onClick={onPlayerClick}
      {...props}
    >
      {player ? (
        <PlayerInfo player={player} inLineup={inLineup} flex={1} />
      ) : (
        <PlayerPlaceholder position={placeholderPosition!} />
      )}

      {action}
    </Flex>
  );
}

interface PlayerInfoProps extends FlexProps {
  player: Player;
  inLineup: boolean;
}

export const PlayerInfo = ({player, inLineup, ...props}: PlayerInfoProps) => {
  const format = useIntl().formatNumber;

  const t = useTranslate();

  const isInvalidPlayer = !!player.selection_status && player.selection_status.status !== 'valid';

  return (
    <Flex align="center" justify="space-between" {...props}>
      {isInvalidPlayer ? (
        <PlayerWarningIndicator player={player.common_name} pos="static" boxSize={[5, 6]} />
      ) : player.is_captain || player.is_vice_captain ? (
        <CaptainIndicator status={player.is_captain ? 'captain' : 'vice-captain'} pos="static" boxSize={[5, 6]} />
      ) : null}

      <Image
        boxSize="9"
        objectFit="contain"
        rounded="full"
        src={player.image?.thumb ?? player.image?.original}
        alt={player.common_name}
      />

      <Flex flex={1} flexDir="column" gap="1" mx="1">
        <Text fontSize="sm" fontWeight="600" textTransform="capitalize" noOfLines={2}>
          {player.common_name}
        </Text>

        <Text fontSize="xs" color="gray.500" noOfLines={1}>
          {t(`fantasy.lineup.${player.position}`)} ٠ {player.team.name}
        </Text>
      </Flex>

      <Flex flexDir="column" gap="0.5" fontSize="sm" fontWeight="700" textAlign="end">
        <Text color="green.400">
          {format((inLineup ? player.scored_fantasy_points : player.fantasy_points) ?? 0, {maximumFractionDigits: 2})}{' '}
          {t('app.points')}
        </Text>

        <Text>{t('fantasy.lineup.price-unit', {amount: format(player.price)})}</Text>
      </Flex>
    </Flex>
  );
};

interface PlayerPlaceholderProps {
  position: Player['position'];
}

const PlayerPlaceholder = ({position}: PlayerPlaceholderProps) => {
  const t = useTranslate();

  return (
    <Flex align="center" gap="3">
      <TShirtIcon filled={false} boxSize="9" />

      <Text fontSize="sm" fontWeight="600">
        {t(`fantasy.lineup.${position}`)}
      </Text>
    </Flex>
  );
};
